import { useEffect, useState } from 'react';
import { formatRelativeAge } from '../lib/format';
import styles from './RefreshStatus.module.css';

function RefreshIcon({ spinning }: { spinning: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      aria-hidden="true"
      focusable="false"
      className={spinning ? styles.spin : undefined}
    >
      <path
        d="M20 12a8 8 0 1 1-2.34-5.66M20 4v4h-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Replaces a big "Refresh" button on pages that already refresh themselves:
 * a quiet "Updated 12s ago" that ticks along, and refreshes when tapped.
 */
export function RefreshStatus({
  updatedAt,
  isFetching,
  isError = false,
  onRefresh,
}: {
  /** Epoch ms of the last successful update (0 or undefined if none yet). */
  updatedAt: number | undefined;
  isFetching: boolean;
  /** The most recent attempt failed. */
  isError?: boolean;
  onRefresh: () => void;
}) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 5_000);
    return () => window.clearInterval(id);
  }, []);

  const age = updatedAt ? formatRelativeAge(new Date(updatedAt).toISOString()) : null;
  const failed = isError && !isFetching;
  const text = isFetching
    ? 'Updating…'
    : failed
      ? age
        ? `Update failed, last ${age}`
        : "Couldn't update. Retry"
      : age
        ? `Updated ${age}`
        : 'Loading…';

  return (
    <button
      type="button"
      className={failed ? `${styles.status} ${styles.failed}` : styles.status}
      // aria-disabled rather than disabled, so a keyboard user's focus stays
      // on the control while it refreshes instead of dropping to the page.
      aria-disabled={isFetching}
      onClick={() => {
        if (!isFetching) onRefresh();
      }}
      aria-label={`Refresh. ${text}`}
    >
      <RefreshIcon spinning={isFetching} />
      <span aria-hidden="true">{text}</span>
    </button>
  );
}
